import { RapportoIntervento, RapportoInterventoForm, RapportoStatoType, Commessa, Sottocommessa, AllegatoFoto, AllegatoFotoForm, TipoAllegato, InvioRapportiResponse } from '../models/types';
import { commessaService, sottocommessaService, rapportiOreService } from './api';
import { logService, LogCategory } from './logService';

// Chiave per localStorage
const RAPPORTI_KEY = 'pipeline_rapporti';
const COMMESSE_CACHE_KEY = 'pipeline_commesse_cache';
const SOTTOCOMMESSE_CACHE_KEY = 'pipeline_sottocommesse_cache';

// Funzione per generare UUID senza dipendenze esterne
const generateUUID = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

// =============================================================================
// Servizio Rapporti Intervento (offline-first con localStorage)
// =============================================================================

export const rapportiService = {
  /**
   * Ottiene tutti i rapporti dal localStorage
   */
  getAll: (): RapportoIntervento[] => {
    const stored = localStorage.getItem(RAPPORTI_KEY);
    if (stored) {
      try {
        const rapporti = JSON.parse(stored) as RapportoIntervento[];
        // Assicura che ogni rapporto abbia l'array allegati
        return rapporti.map(r => ({ ...r, allegati: r.allegati || [] }));
      } catch {
        return [];
      }
    }
    return [];
  },

  /**
   * Ottiene un rapporto per ID
   */
  getById: (id: string): RapportoIntervento | undefined => {
    const rapporti = rapportiService.getAll();
    return rapporti.find(r => r.id === id);
  },

  /**
   * Crea un nuovo rapporto
   */
  create: (form: RapportoInterventoForm): RapportoIntervento => {
    const now = new Date().toISOString();
    const rapporto: RapportoIntervento = {
      id: generateUUID(),
      ...form,
      stato: 'draft',
      allegati: [],
      createdAt: now,
      updatedAt: now,
    };

    const rapporti = rapportiService.getAll();
    rapporti.unshift(rapporto); // Aggiungi in cima
    localStorage.setItem(RAPPORTI_KEY, JSON.stringify(rapporti));

    return rapporto;
  },

  /**
   * Aggiorna un rapporto esistente
   */
  update: (id: string, updates: Partial<RapportoIntervento>): RapportoIntervento | undefined => {
    const rapporti = rapportiService.getAll();
    const index = rapporti.findIndex(r => r.id === id);
    
    if (index === -1) return undefined;

    rapporti[index] = {
      ...rapporti[index],
      ...updates,
      updatedAt: new Date().toISOString(),
    };

    localStorage.setItem(RAPPORTI_KEY, JSON.stringify(rapporti));
    return rapporti[index];
  },

  /**
   * Elimina un rapporto
   */
  delete: (id: string): boolean => {
    const rapporti = rapportiService.getAll();
    const index = rapporti.findIndex(r => r.id === id);
    
    if (index === -1) return false;

    rapporti.splice(index, 1);
    localStorage.setItem(RAPPORTI_KEY, JSON.stringify(rapporti));
    return true;
  },

  /**
   * Cambia lo stato del rapporto (draft <-> pronto)
   */
  toggleStato: (id: string): RapportoIntervento | undefined => {
    const rapporto = rapportiService.getById(id);
    if (!rapporto) return undefined;

    const nuovoStato: RapportoStatoType = rapporto.stato === 'draft' ? 'pronto' : 'draft';
    return rapportiService.update(id, { stato: nuovoStato });
  },

  /**
   * Ottiene i rapporti filtrati per stato
   */
  getByStato: (stato: RapportoStatoType): RapportoIntervento[] => {
    return rapportiService.getAll().filter(r => r.stato === stato);
  },

  /**
   * Ottiene i rapporti non ancora sincronizzati
   */
  getNonSincronizzati: (): RapportoIntervento[] => {
    return rapportiService.getAll().filter(r => !r.syncedAt);
  },

  /**
   * Aggiunge un allegato foto a un rapporto
   */
  addAllegato: (rapportoId: string, allegatoForm: AllegatoFotoForm): AllegatoFoto | undefined => {
    const rapporto = rapportiService.getById(rapportoId);
    if (!rapporto) return undefined;

    const allegato: AllegatoFoto = {
      id: generateUUID(),
      rapportoId,
      ...allegatoForm,
      createdAt: new Date().toISOString(),
    };

    const allegati = [...rapporto.allegati, allegato];
    rapportiService.update(rapportoId, { allegati });

    return allegato;
  },

  /**
   * Rimuove un allegato foto da un rapporto
   */
  removeAllegato: (rapportoId: string, allegatoId: string): boolean => {
    const rapporto = rapportiService.getById(rapportoId);
    if (!rapporto) return false;

    const allegati = rapporto.allegati.filter(a => a.id !== allegatoId);
    if (allegati.length === rapporto.allegati.length) return false;

    rapportiService.update(rapportoId, { allegati });
    return true;
  },

  /**
   * Aggiorna un allegato esistente
   */
  updateAllegato: (rapportoId: string, allegatoId: string, updates: Partial<AllegatoFoto>): AllegatoFoto | undefined => {
    const rapporto = rapportiService.getById(rapportoId);
    if (!rapporto) return undefined;

    const index = rapporto.allegati.findIndex(a => a.id === allegatoId);
    if (index === -1) return undefined;

    rapporto.allegati[index] = { ...rapporto.allegati[index], ...updates };
    rapportiService.update(rapportoId, { allegati: rapporto.allegati });

    return rapporto.allegati[index];
  },

  /**
   * Invia i rapporti pronti al backend e rimuove quelli sincronizzati con successo
   * @returns Risultato dell'invio
   */
  inviaRapportiPronti: async (): Promise<InvioRapportiResponse> => {
    const rapportiPronti = rapportiService.getByStato('pronto');
    
    if (rapportiPronti.length === 0) {
      logService.info(LogCategory.SYNC, 'Nessun rapporto pronto da inviare');
      return {
        totaleRicevuti: 0,
        salvati: 0,
        duplicati: 0,
        errori: 0,
        risultati: [],
      };
    }

    logService.info(LogCategory.SYNC, `Avvio invio di ${rapportiPronti.length} rapporti`);

    // Invia al backend - gli errori HTTP vengono loggati dall'interceptor API
    const response = await rapportiOreService.inviaRapporti(rapportiPronti);

    // Raccogli gli ID dei rapporti sincronizzati con successo (salvati o duplicati)
    const idDaRimuovere: string[] = [];
    
    for (const risultato of response.risultati) {
      if (risultato.stato === 'salvato' || risultato.stato === 'duplicato') {
        // Rapporto sincronizzato: da rimuovere dal localStorage
        idDaRimuovere.push(risultato.idLocale);
      } else if (risultato.stato === 'errore') {
        // Log errore specifico per ogni rapporto fallito (errore di business logic, non HTTP)
        logService.error(
          LogCategory.SYNC, 
          `Errore invio rapporto ${risultato.idLocale}`,
          risultato.errore || 'Errore sconosciuto'
        );
      }
    }

    // Rimuovi i rapporti sincronizzati dal localStorage
    if (idDaRimuovere.length > 0) {
      const rapportiAttuali = rapportiService.getAll();
      const rapportiDaMantenere = rapportiAttuali.filter(r => !idDaRimuovere.includes(r.id));
      localStorage.setItem(RAPPORTI_KEY, JSON.stringify(rapportiDaMantenere));
      
      logService.info(
        LogCategory.SYNC, 
        `Rimossi ${idDaRimuovere.length} rapporti sincronizzati dal dispositivo`
      );

      // Notifica i componenti che i rapporti sono stati aggiornati
      window.dispatchEvent(new CustomEvent('rapporti-sync'));
    }

    // Log del risultato
    if (response.errori > 0) {
      logService.warn(LogCategory.SYNC, 
        `Invio completato con ${response.errori} errori`, 
        `Salvati: ${response.salvati}, Duplicati: ${response.duplicati}, Errori: ${response.errori}`
      );
    } else {
      logService.success(LogCategory.SYNC, 
        `Invio completato con successo`, 
        `Salvati: ${response.salvati}, Duplicati: ${response.duplicati}`
      );
    }

    return response;
  },

  /**
   * Invia un singolo rapporto al backend e lo rimuove se sincronizzato con successo
   */
  inviaRapporto: async (id: string): Promise<boolean> => {
    const rapporto = rapportiService.getById(id);
    if (!rapporto) {
      logService.error(LogCategory.SYNC, 'Rapporto non trovato per invio', `ID: ${id}`);
      throw new Error('Rapporto non trovato');
    }

    logService.info(LogCategory.SYNC, `Invio rapporto singolo`, `ID: ${id}`);
    
    // Gli errori HTTP vengono loggati dall'interceptor API
    const response = await rapportiOreService.inviaRapporto(rapporto);
    
    if (response.success) {
      // Rimuovi il rapporto sincronizzato dal localStorage
      rapportiService.delete(id);
      logService.success(LogCategory.SYNC, `Rapporto inviato e rimosso dal dispositivo`, `ID: ${id}`);
      
      // Notifica i componenti che i rapporti sono stati aggiornati
      window.dispatchEvent(new CustomEvent('rapporti-sync'));
      return true;
    }
    
    logService.warn(LogCategory.SYNC, `Invio rapporto fallito`, `ID: ${id}, Messaggio: ${response.message}`);
    return false;
  },

  /**
   * Ottiene il conteggio dei rapporti pronti per l'invio
   */
  getConteggioPronti: (): number => {
    return rapportiService.getByStato('pronto').filter(r => !r.syncedAt).length;
  },

  /**
   * Verifica lo stato di sincronizzazione con il backend
   */
  verificaSincronizzazione: async (): Promise<void> => {
    const rapportiNonSync = rapportiService.getNonSincronizzati();
    if (rapportiNonSync.length === 0) return;

    const idLocali = rapportiNonSync.map(r => r.id);
    const statoServer = await rapportiOreService.verificaSincronizzazione(idLocali);

    const now = new Date().toISOString();
    for (const [idLocale, sincronizzato] of Object.entries(statoServer)) {
      if (sincronizzato) {
        rapportiService.update(idLocale, { syncedAt: now });
      }
    }
  },

  /**
   * Rimuove i rapporti gi� sincronizzati (pulizia)
   */
  rimuoviSincronizzati: (): number => {
    const rapporti = rapportiService.getAll();
    const daSincronizzare = rapporti.filter(r => !r.syncedAt);
    const rimossi = rapporti.length - daSincronizzare.length;
    
    localStorage.setItem(RAPPORTI_KEY, JSON.stringify(daSincronizzare));
    
    return rimossi;
  },
};

// =============================================================================
// Servizio Tipi Allegato (mock - in futuro dal backend)
// =============================================================================

const DEMO_TIPI_ALLEGATO: TipoAllegato[] = [
  { id: 1, codice: 'FOTO_LAVORO', nome: 'Foto lavoro eseguito' },
  { id: 2, codice: 'FOTO_PROBLEMA', nome: 'Foto problema riscontrato' },
  { id: 3, codice: 'FOTO_MATERIALE', nome: 'Foto materiale utilizzato' },
  { id: 4, codice: 'FOTO_PRIMA', nome: 'Foto prima intervento' },
  { id: 5, codice: 'FOTO_DOPO', nome: 'Foto dopo intervento' },
  { id: 6, codice: 'DOCUMENTO', nome: 'Documento/Etichetta' },
  { id: 7, codice: 'ALTRO', nome: 'Altro' },
];

export const tipiAllegatoService = {
  /**
   * Ottiene tutti i tipi di allegato
   */
  getAll: (): TipoAllegato[] => {
    return DEMO_TIPI_ALLEGATO;
  },

  /**
   * Ottiene un tipo allegato per ID
   */
  getById: (id: number): TipoAllegato | undefined => {
    return DEMO_TIPI_ALLEGATO.find(t => t.id === id);
  },
};

// =============================================================================
// Servizio Commesse (API con cache locale per offline)
// =============================================================================

export const commesseService = {
  /**
   * Ottiene tutte le commesse aperte con ricerca
   * Chiama l'API e aggiorna la cache locale
   */
  getCommesseAperte: async (search?: string): Promise<Commessa[]> => {
    try {
      const commesse = await commessaService.getCommesseAperte(search);
      // Aggiorna cache solo se non c'� ricerca (cache completa)
      if (!search) {
        localStorage.setItem(COMMESSE_CACHE_KEY, JSON.stringify(commesse));
      }
      return commesse;
    } catch (error) {
      console.warn('Impossibile caricare commesse da API, uso cache locale', error);
      // Fallback alla cache locale
      return commesseService.getFromCache(search);
    }
  },

  /**
   * Ottiene tutte le commesse (anche chiuse) con ricerca
   */
  getAll: async (search?: string, includiChiuse: boolean = false): Promise<Commessa[]> => {
    try {
      return await commessaService.getTutte(search, includiChiuse);
    } catch (error) {
      console.warn('Impossibile caricare commesse da API, uso cache locale', error);
      const cached = commesseService.getFromCache(search);
      if (includiChiuse) {
        return cached;
      }
      return cached.filter(c => !c.chiusa);
    }
  },

  /**
   * Ottiene una commessa per ID
   */
  getById: async (id: number): Promise<Commessa | undefined> => {
    try {
      const commessa = await commessaService.getById(id);
      return commessa || undefined;
    } catch {
      // Fallback alla cache
      return commesseService.getFromCacheById(id);
    }
  },

  /**
   * Ottiene commesse dalla cache locale (per uso offline)
   */
  getFromCache: (search?: string): Commessa[] => {
    const stored = localStorage.getItem(COMMESSE_CACHE_KEY);
    if (!stored) return [];
    try {
      let commesse = JSON.parse(stored) as Commessa[];
      if (search) {
        const searchLower = search.toLowerCase();
        commesse = commesse.filter(c =>
          c.titolo?.toLowerCase().includes(searchLower) ||
          c.impiantoNome?.toLowerCase().includes(searchLower) ||
          c.anagraficaNome?.toLowerCase().includes(searchLower) ||
          c.numero.toString().includes(search)
        );
      }
      return commesse;
    } catch {
      return [];
    }
  },

  /**
   * Ottiene una commessa dalla cache per ID
   */
  getFromCacheById: (id: number): Commessa | undefined => {
    const commesse = commesseService.getFromCache();
    return commesse.find(c => c.id === id);
  },
};

// =============================================================================
// Servizio Sottocommesse (API con cache locale per offline)
// =============================================================================

export const sottocommesseService = {
  /**
   * Ottiene le sottocommesse di una commessa con ricerca
   * Chiama l'API e aggiorna la cache locale
   */
  getByCommessa: async (commessaId: number, search?: string): Promise<Sottocommessa[]> => {
    try {
      const sottocommesse = await sottocommessaService.getByCommessa(commessaId, search);
      // Aggiorna cache per questa commessa
      if (!search) {
        sottocommesseService.updateCache(commessaId, sottocommesse);
      }
      return sottocommesse;
    } catch (error) {
      console.warn('Impossibile caricare sottocommesse da API, uso cache locale', error);
      // Fallback alla cache locale
      return sottocommesseService.getFromCache(commessaId, search);
    }
  },

  /**
   * Ottiene tutte le sottocommesse dalla cache
   */
  getAll: (): Sottocommessa[] => {
    return sottocommesseService.getFromCache();
  },

  /**
   * Ottiene una sottocommessa per ID
   */
  getById: async (id: number): Promise<Sottocommessa | undefined> => {
    try {
      const sottocommessa = await sottocommessaService.getById(id);
      return sottocommessa || undefined;
    } catch {
      // Fallback alla cache
      return sottocommesseService.getFromCacheById(id);
    }
  },

  /**
   * Aggiorna la cache locale per una commessa
   */
  updateCache: (commessaId: number, sottocommesse: Sottocommessa[]): void => {
    const stored = localStorage.getItem(SOTTOCOMMESSE_CACHE_KEY);
    let cache: { [key: number]: Sottocommessa[] } = {};
    if (stored) {
      try {
        cache = JSON.parse(stored);
      } catch { /* ignore */ }
    }
    cache[commessaId] = sottocommesse;
    localStorage.setItem(SOTTOCOMMESSE_CACHE_KEY, JSON.stringify(cache));
  },

  /**
   * Ottiene sottocommesse dalla cache locale (per uso offline)
   */
  getFromCache: (commessaId?: number, search?: string): Sottocommessa[] => {
    const stored = localStorage.getItem(SOTTOCOMMESSE_CACHE_KEY);
    if (!stored) return [];
    try {
      const cache = JSON.parse(stored) as { [key: number]: Sottocommessa[] };
      let sottocommesse: Sottocommessa[] = [];
      
      if (commessaId) {
        sottocommesse = cache[commessaId] || [];
      } else {
        // Unisce tutte le sottocommesse di tutte le commesse
        sottocommesse = Object.values(cache).flat();
      }

      if (search) {
        const searchLower = search.toLowerCase();
        sottocommesse = sottocommesse.filter(s =>
          s.codice?.toLowerCase().includes(searchLower) ||
          s.nome?.toLowerCase().includes(searchLower)
        );
      }
      return sottocommesse;
    } catch {
      return [];
    }
  },

  /**
   * Ottiene una sottocommessa dalla cache per ID
   */
  getFromCacheById: (id: number): Sottocommessa | undefined => {
    const sottocommesse = sottocommesseService.getFromCache();
    return sottocommesse.find(s => s.id === id);
  },
};

export default rapportiService;
